/**
 * API Client for FeedbackHub
 * Handles all HTTP requests to the backend API
 */

import type { TopPost } from '@feedbackhub/types';
import { captureException } from '@/lib/sentry';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Re-export for convenience
export type { TopPost };

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  code?: string;
}

// CSRF token expiry time in milliseconds (15 minutes)
const CSRF_TOKEN_TTL = 15 * 60 * 1000;

class ApiClient {
  private baseURL: string;
  private csrfToken: string | null = null;
  private csrfTokenFetchedAt: number = 0;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Check if the cached CSRF token is still valid
   */
  private isCsrfTokenValid(): boolean {
    if (!this.csrfToken) {
      return false;
    }
    const tokenAge = Date.now() - this.csrfTokenFetchedAt;
    return tokenAge < CSRF_TOKEN_TTL;
  }

  /**
   * Fetch CSRF token from the server
   * Tokens are cached for 15 minutes to balance security and performance
   */
  private async fetchCsrfToken(): Promise<string> {
    // Return cached token if still valid
    if (this.isCsrfTokenValid()) {
      return this.csrfToken!;
    }

    try {
      const response = await fetch(`${this.baseURL}/api/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch CSRF token');
      }

      const data = await response.json();
      this.csrfToken = data.data?.csrfToken || data.csrfToken;
      this.csrfTokenFetchedAt = Date.now();
      return this.csrfToken!;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      throw error;
    }
  }

  /**
   * Clear cached CSRF token (call after logout or on token errors)
   */
  clearCsrfToken(): void {
    this.csrfToken = null;
    this.csrfTokenFetchedAt = 0;
  }

  /**
   * Initialize CSRF protection by pre-fetching a token.
   * Call this early in app lifecycle to ensure cookie is established.
   */
  async initializeCsrf(): Promise<void> {
    try {
      await this.fetchCsrfToken();
    } catch {
      // Ignore errors - CSRF will be fetched on first request
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const method = options.method || 'GET';

    // For state-changing methods, include CSRF token
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method.toUpperCase())) {
      try {
        const csrfToken = await this.fetchCsrfToken();
        headers['X-CSRF-Token'] = csrfToken;
      } catch {
        // Continue without CSRF token - server will reject if required
        console.warn('Could not fetch CSRF token');
      }
    }

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'include', // Important for session cookies
    };

    try {
      const response = await fetch(url, config);
      const data: ApiResponse<T> = await response.json();

      // If CSRF token is invalid, clear it and retry once
      if (response.status === 403 && data.code === 'CSRF_VALIDATION_FAILED') {
        this.clearCsrfToken();
        // Retry the request with a fresh token
        const newCsrfToken = await this.fetchCsrfToken();
        const retryHeaders = {
          ...headers,
          'X-CSRF-Token': newCsrfToken,
        };
        const retryConfig: RequestInit = {
          method,
          headers: retryHeaders,
          credentials: 'include',
          body: options.body, // Body is already stringified
        };
        const retryResponse = await fetch(url, retryConfig);
        const retryData: ApiResponse<T> = await retryResponse.json();

        if (!retryResponse.ok) {
          throw new Error(retryData.error || retryData.message || 'An error occurred');
        }
        if (!retryData.success) {
          throw new Error(retryData.error || 'Request failed');
        }
        return retryData.data as T;
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || 'An error occurred');
      }

      if (!data.success) {
        throw new Error(data.error || 'Request failed');
      }

      return data.data as T;
    } catch (error) {
      // Capture non-auth errors to Sentry
      if (error instanceof Error) {
        const isAuthError = error.message.includes('401') ||
          error.message.toLowerCase().includes('authentication') ||
          error.message.toLowerCase().includes('unauthorized');
        if (!isAuthError) {
          captureException(error, { endpoint, method });
        }
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Auth endpoints
  auth = {
    register: (data: { email: string; password: string; name: string }) =>
      this.request<{
        email?: string;
        requiresVerification?: boolean;
        requiresMigrationDecision?: boolean;
        existingData?: {
          alias: string;
          postsCount: number;
          commentsCount: number;
          votesCount: number;
          memberSince: string;
        };
      }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    migrationDecision: (data: {
      email: string;
      password: string;
      name: string;
      decision: 'accept' | 'decline';
    }) =>
      this.request<{ email: string; requiresVerification: boolean }>(
        '/api/auth/migration-decision',
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      ),

    login: (data: { email: string; password: string }) =>
      this.request<{ user: any }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    logout: () =>
      this.request('/api/auth/logout', {
        method: 'POST',
      }),

    me: () => this.request<{ user: any }>('/api/auth/me'),

    forgotPassword: (data: { email: string }) =>
      this.request<{ message: string }>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    resetPassword: (data: { token: string; password: string }) =>
      this.request<{ message: string }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    verifyEmail: (token: string) =>
      this.request<{ message: string; user?: any }>(`/api/auth/verify-email?token=${encodeURIComponent(token)}`),
  };

  // Board endpoints
  boards = {
    list: () =>
      this.request<{ boards: any[] }>('/api/boards'),

    get: (slug: string) =>
      this.request<{ board: any }>(`/api/boards/${slug}`),

    create: (data: { name: string; description?: string; isPrivate?: boolean }) =>
      this.request<{ board: any }>('/api/boards', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (slug: string, data: Partial<{ name: string; description: string; isPrivate: boolean }>) =>
      this.request<{ board: any }>(`/api/boards/${slug}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (slug: string) =>
      this.request(`/api/boards/${slug}`, {
        method: 'DELETE',
      }),
  };

  // Post endpoints
  posts = {
    list: (params?: {
      boardId?: string;
      categoryId?: string;
      status?: string;
      authorId?: string;
      sort?: string;
      page?: number;
      limit?: number;
    }) => {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, String(value));
          }
        });
      }
      const query = queryParams.toString();
      return this.request<{ posts: any[]; pagination: any }>(
        `/api/posts${query ? `?${query}` : ''}`
      );
    },

    get: (slug: string) =>
      this.request<{ post: any }>(`/api/posts/${slug}`),

    create: (data: {
      title: string;
      details?: string;
      boardId: string;
      categoryId?: string;
      imageURLs?: string[];
      allowContact?: boolean;
    }) =>
      this.request<{ post: any }>('/api/posts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (slug: string, data: Partial<{
      title: string;
      details: string;
      status: string;
      categoryId: string;
      estimatedCompletionDate: string | null;
    }>) =>
      this.request<{ post: any }>(`/api/posts/${slug}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    delete: (slug: string) =>
      this.request(`/api/posts/${slug}`, {
        method: 'DELETE',
      }),

    similar: (params: { title?: string; boardId: string; limit?: number }) => {
      const queryParams = new URLSearchParams();
      queryParams.append('boardId', params.boardId);
      if (params.title !== undefined) {
        queryParams.append('title', params.title);
      }
      if (params.limit !== undefined) {
        queryParams.append('limit', String(params.limit));
      }
      return this.request<{
        posts: Array<{
          id: string;
          title: string;
          slug: string;
          status: string;
          score: number;
          similarityScore?: number;
        }>;
        type: 'recent' | 'similar';
      }>(`/api/posts/similar?${queryParams.toString()}`);
    },
  };

  // Comment endpoints
  comments = {
    list: (postId: string) =>
      this.request<{ comments: any[] }>(`/api/comments?postId=${postId}`),

    create: (data: {
      postId: string;
      value: string;
      parentId?: string;
      internal?: boolean;
      imageURLs?: string[];
    }) =>
      this.request<{ comment: any }>('/api/comments', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    update: (id: string, data: { value: string }) =>
      this.request<{ comment: any }>(`/api/comments/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),

    delete: (id: string) =>
      this.request(`/api/comments/${id}`, {
        method: 'DELETE',
      }),
  };

  // Vote endpoints
  votes = {
    toggle: (postId: string) =>
      this.request<{ voted: boolean; score: number }>('/api/votes', {
        method: 'POST',
        body: JSON.stringify({ postId }),
      }),

    check: (postId: string) =>
      this.request<{ voted: boolean }>(`/api/votes/${postId}/check`),

    voters: (postId: string, params?: { page?: number; limit?: number }) => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      const query = queryParams.toString();
      return this.request<{
        voters: Array<{
          id: string;
          name: string;
          alias: string | null;
          avatarURL: string | null;
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`/api/votes/${postId}/voters${query ? `?${query}` : ''}`);
    },
  };

  // Admin endpoints
  admin = {
    stats: () =>
      this.request<{
        stats: {
          totalUsers: number;
          totalPosts: number;
          totalComments: number;
          totalVotes: number;
        };
        postsByStatus: Record<string, number>;
        topPosts: TopPost[];
      }>('/api/admin/stats'),

    users: {
      list: (params?: {
        page?: number;
        limit?: number;
        search?: string;
        sort?: 'newest' | 'oldest' | 'most_active' | 'most_posts' | 'most_votes';
        hasActivity?: string[];
        dateFrom?: string;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.limit) queryParams.append('limit', String(params.limit));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.sort) queryParams.append('sort', params.sort);
        if (params?.hasActivity?.length) queryParams.append('hasActivity', params.hasActivity.join(','));
        if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        const query = queryParams.toString();
        return this.request<{
          users: any[];
          pagination: { page: number; limit: number; total: number; pages: number };
        }>(`/api/admin/users${query ? `?${query}` : ''}`);
      },

      get: (id: string) =>
        this.request<{ user: any }>(`/api/admin/users/${id}`),

      update: (id: string, data: { isAdmin?: boolean; adminRole?: string | null }) =>
        this.request<{ user: any }>(`/api/admin/users/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      delete: (id: string) =>
        this.request(`/api/admin/users/${id}`, {
          method: 'DELETE',
        }),

      comments: (id: string, params?: { limit?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.append('limit', String(params.limit));
        const query = queryParams.toString();
        return this.request<{
          comments: Array<{
            id: string;
            value: string;
            created: string;
            post: {
              id: string;
              title: string;
              slug: string;
              board: {
                id: string;
                name: string;
                slug: string;
              };
            };
          }>;
        }>(`/api/admin/users/${id}/comments${query ? `?${query}` : ''}`);
      },

      votes: (id: string, params?: { limit?: number }) => {
        const queryParams = new URLSearchParams();
        if (params?.limit) queryParams.append('limit', String(params.limit));
        const query = queryParams.toString();
        return this.request<{
          votes: Array<{
            id: string;
            created: string;
            post: {
              id: string;
              title: string;
              slug: string;
              status: string;
              score: number;
              board: {
                id: string;
                name: string;
                slug: string;
              };
            };
          }>;
        }>(`/api/admin/users/${id}/votes${query ? `?${query}` : ''}`);
      },

      getLockout: (email: string) =>
        this.request<{
          email: string;
          isLocked: boolean;
          failedAttempts: number;
          lockExpiresAt?: string;
        }>(`/api/admin/account-lockout/${encodeURIComponent(email)}`),

      unlock: (email: string) =>
        this.request<{ message: string }>(`/api/admin/account-lockout/${encodeURIComponent(email)}/unlock`, {
          method: 'POST',
        }),
    },

    posts: {
      list: (params?: {
        page?: number;
        limit?: number;
        status?: string;
        statuses?: string[];
        boardId?: string;
        boards?: string[];
        search?: string;
        sort?: 'newest' | 'oldest' | 'trending' | 'top';
        dateFrom?: string;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.limit) queryParams.append('limit', String(params.limit));
        if (params?.status) queryParams.append('status', params.status);
        if (params?.statuses?.length) queryParams.append('statuses', params.statuses.join(','));
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        if (params?.boards?.length) queryParams.append('boards', params.boards.join(','));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.sort) queryParams.append('sort', params.sort);
        if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        const query = queryParams.toString();
        return this.request<{
          posts: any[];
          pagination: { page: number; limit: number; total: number; pages: number };
        }>(`/api/admin/posts${query ? `?${query}` : ''}`);
      },

      getIds: (params?: {
        status?: string;
        statuses?: string[];
        boardId?: string;
        boards?: string[];
        search?: string;
        dateFrom?: string;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.status) queryParams.append('status', params.status);
        if (params?.statuses?.length) queryParams.append('statuses', params.statuses.join(','));
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        if (params?.boards?.length) queryParams.append('boards', params.boards.join(','));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        const query = queryParams.toString();
        return this.request<{
          postIds: string[];
          total: number;
          limited: boolean;
        }>(`/api/admin/posts/ids${query ? `?${query}` : ''}`);
      },

      bulk: (params: {
        postIds: string[];
        operation: 'status' | 'delete' | 'category';
        value?: string;
      }) =>
        this.request<{ updated: number; deleted?: number }>('/api/admin/posts/bulk', {
          method: 'POST',
          body: JSON.stringify(params),
        }),
    },

    auditLogs: {
      list: (params?: {
        page?: number;
        limit?: number;
        search?: string;
        action?: string;
        entityType?: string;
        userId?: string;
        sort?: 'newest' | 'oldest';
        dateFrom?: string;
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.limit) queryParams.append('limit', String(params.limit));
        if (params?.search) queryParams.append('search', params.search);
        if (params?.action) queryParams.append('action', params.action);
        if (params?.entityType) queryParams.append('entityType', params.entityType);
        if (params?.userId) queryParams.append('userId', params.userId);
        if (params?.sort) queryParams.append('sort', params.sort);
        if (params?.dateFrom) queryParams.append('dateFrom', params.dateFrom);
        const query = queryParams.toString();
        return this.request<{
          auditLogs: Array<{
            id: string;
            action: string;
            entityType: string;
            entityId: string;
            details: Record<string, unknown> | null;
            ipAddress: string | null;
            userAgent: string | null;
            created: string;
            user: {
              id: string;
              name: string;
              email: string;
              alias: string;
              avatarURL: string | null;
            };
          }>;
          pagination: { page: number; limit: number; total: number; pages: number };
        }>(`/api/admin/audit-logs${query ? `?${query}` : ''}`);
      },
    },

    analytics: {
      overview: (params?: { period?: string; boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return this.request<{
          posts: { value: number; trend: number };
          votes: { value: number; trend: number };
          comments: { value: number; trend: number };
          users: { value: number; trend: number };
          period: string;
          dateRange: { start: string; end: string };
        }>(`/api/admin/analytics/overview${query ? `?${query}` : ''}`);
      },

      timeSeries: (params?: { period?: string; boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return this.request<{
          series: Array<{ date: string; posts: number; votes: number; comments: number }>;
          interval: string;
          period: string;
        }>(`/api/admin/analytics/time-series${query ? `?${query}` : ''}`);
      },

      statusTrends: (params?: { period?: string; boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return this.request<{
          distribution: Array<{ status: string; count: number; percentage: number }>;
          total: number;
          period: string;
        }>(`/api/admin/analytics/status-trends${query ? `?${query}` : ''}`);
      },

      categories: (params?: { period?: string; boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return this.request<{
          categories: Array<{
            id: string;
            name: string;
            color: string | null;
            postCount: number;
            totalVotes: number;
          }>;
        }>(`/api/admin/analytics/categories${query ? `?${query}` : ''}`);
      },

      activityHeatmap: (params?: { period?: string; boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return this.request<{
          heatmap: Array<{
            day: string;
            dayIndex: number;
            hour: number;
            count: number;
            intensity: number;
          }>;
          maxCount: number;
          period: string;
        }>(`/api/admin/analytics/activity-heatmap${query ? `?${query}` : ''}`);
      },

      engagement: (params?: { period?: string; boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return this.request<{
          avgCommentsPerPost: number;
          avgVotesPerPost: number;
          totalPosts: number;
          totalComments: number;
          totalVotes: number;
          topEngaged: Array<{
            id: string;
            title: string;
            slug: string;
            commentCount: number;
            score: number;
            created: string;
          }>;
          period: string;
        }>(`/api/admin/analytics/engagement${query ? `?${query}` : ''}`);
      },

      users: (params?: { period?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        const query = queryParams.toString();
        return this.request<{
          topPosters: Array<{
            id: string;
            name: string;
            alias: string;
            avatarURL: string | null;
            count: number;
          }>;
          topVoters: Array<{
            id: string;
            name: string;
            alias: string;
            avatarURL: string | null;
            count: number;
          }>;
          topCommenters: Array<{
            id: string;
            name: string;
            alias: string;
            avatarURL: string | null;
            count: number;
          }>;
          period: string;
        }>(`/api/admin/analytics/users${query ? `?${query}` : ''}`);
      },

      boards: (params?: { period?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        const query = queryParams.toString();
        return this.request<{
          boards: Array<{
            id: string;
            name: string;
            slug: string;
            totalPosts: number;
            newPosts: number;
            totalVotes: number;
            totalComments: number;
          }>;
          period: string;
        }>(`/api/admin/analytics/boards${query ? `?${query}` : ''}`);
      },

      sla: (params?: { boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return this.request<{
          distribution: { on_track: number; at_risk: number; overdue: number };
          overdueByStatus: { open: number; under_review: number; in_progress: number };
          overduePosts: Array<{
            id: string;
            title: string;
            slug: string;
            status: string;
            slaDeadline: string;
            score: number;
            board: { id: string; name: string; slug: string };
          }>;
          totalWithSla: number;
          avgResolutionDays: number | null;
          complianceRate: number;
        }>(`/api/admin/analytics/sla${query ? `?${query}` : ''}`);
      },

      exportUrl: (params?: { period?: string; boardId?: string }) => {
        const queryParams = new URLSearchParams();
        if (params?.period) queryParams.append('period', params.period);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        const query = queryParams.toString();
        return `/api/admin/analytics/export${query ? `?${query}` : ''}`;
      },
    },

    duplicates: {
      list: (params?: {
        page?: number;
        limit?: number;
        status?: 'pending' | 'reviewed' | 'merged' | 'dismissed' | 'all';
        boardId?: string;
        minSimilarity?: number;
        sort?: 'highest' | 'lowest';
      }) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', String(params.page));
        if (params?.limit) queryParams.append('limit', String(params.limit));
        if (params?.status) queryParams.append('status', params.status);
        if (params?.boardId) queryParams.append('boardId', params.boardId);
        if (params?.minSimilarity) queryParams.append('minSimilarity', String(params.minSimilarity));
        if (params?.sort) queryParams.append('sort', params.sort);
        const query = queryParams.toString();
        return this.request<{
          groups: Array<{
            id: string;
            status: string;
            primaryPost: { id: string; title: string; slug: string; status: string; score: number } | null;
            similarityThreshold: number;
            notes: string | null;
            created: string;
            updated: string;
            postCount: number;
            posts: Array<{
              id: string;
              title: string;
              slug: string;
              status: string;
              score: number;
              created: string;
              board: { id: string; name: string; slug: string };
              similarityScore: number;
              isPrimary: boolean;
            }>;
          }>;
          pagination: { page: number; limit: number; total: number; pages: number };
        }>(`/api/admin/duplicates${query ? `?${query}` : ''}`);
      },

      get: (groupId: string) =>
        this.request<{
          group: {
            id: string;
            status: string;
            primaryPost: any | null;
            similarityThreshold: number;
            notes: string | null;
            created: string;
            updated: string;
            posts: Array<{
              id: string;
              title: string;
              slug: string;
              details: string | null;
              status: string;
              score: number;
              commentCount: number;
              created: string;
              author: { id: string; name: string; alias: string };
              board: { id: string; name: string; slug: string };
              similarityScore: number;
              isPrimary: boolean;
              voteCount: number;
            }>;
          };
        }>(`/api/admin/duplicates/${groupId}`),

      scan: (params?: { boardId?: string; minSimilarity?: number; limit?: number }) =>
        this.request<{
          groupsCreated: number;
          pairsFound?: number;
          message: string;
        }>('/api/admin/duplicates/scan', {
          method: 'POST',
          body: JSON.stringify(params || {}),
        }),

      update: (groupId: string, data: {
        status?: 'pending' | 'reviewed' | 'merged' | 'dismissed';
        primaryPostId?: string;
        notes?: string;
      }) =>
        this.request<{ group: any }>(`/api/admin/duplicates/${groupId}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      merge: (groupId: string, postIds: string[]) =>
        this.request<{
          message: string;
          primaryPostId: string;
          mergedCount: number;
        }>(`/api/admin/duplicates/${groupId}/merge`, {
          method: 'POST',
          body: JSON.stringify({ postIds }),
        }),

      delete: (groupId: string) =>
        this.request(`/api/admin/duplicates/${groupId}`, {
          method: 'DELETE',
        }),
    },

    settings: {
      getSla: () =>
        this.request<{
          open: { days: number; enabled: boolean };
          under_review: { days: number; enabled: boolean };
          in_progress: { days: number; enabled: boolean };
        }>('/api/admin/settings/sla'),

      updateSla: (data: Partial<{
        open: { days: number; enabled: boolean };
        under_review: { days: number; enabled: boolean };
        in_progress: { days: number; enabled: boolean };
      }>) =>
        this.request<{
          open: { days: number; enabled: boolean };
          under_review: { days: number; enabled: boolean };
          in_progress: { days: number; enabled: boolean };
        }>('/api/admin/settings/sla', {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),
    },

    integrations: {
      getSlack: () =>
        this.request<{
          enabled: boolean;
          webhookUrl: string;
          webhookUrlSet: boolean;
          notifyNewPosts: boolean;
          dailySummary: boolean;
          dailySummaryHour: number;
        }>('/api/admin/integrations/slack'),

      updateSlack: (data: Partial<{
        enabled: boolean;
        webhookUrl: string;
        notifyNewPosts: boolean;
        dailySummary: boolean;
        dailySummaryHour: number;
      }>) =>
        this.request<{
          enabled: boolean;
          webhookUrl: string;
          webhookUrlSet: boolean;
          notifyNewPosts: boolean;
          dailySummary: boolean;
          dailySummaryHour: number;
        }>('/api/admin/integrations/slack', {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      testSlack: () =>
        this.request<{ message: string }>('/api/admin/integrations/slack/test', {
          method: 'POST',
        }),

      // Jira integration
      getJira: () =>
        this.request<{
          configured: boolean;
          connected: boolean;
          siteName?: string;
          cloudId?: string;
          enabled: boolean;
          defaultProjectKey?: string | null;
          installedBy?: string;
          connectedAt?: string;
        }>('/api/admin/integrations/jira'),

      updateJira: (data: Partial<{
        enabled: boolean;
        defaultProjectKey: string | null;
      }>) =>
        this.request<{
          configured: boolean;
          connected: boolean;
          siteName?: string;
          cloudId?: string;
          enabled: boolean;
          defaultProjectKey?: string | null;
          installedBy?: string;
          connectedAt?: string;
        }>('/api/admin/integrations/jira', {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      getJiraAuthUrl: () =>
        this.request<{ url: string }>('/api/admin/integrations/jira/auth'),

      disconnectJira: () =>
        this.request<{ message: string }>('/api/admin/integrations/jira/disconnect', {
          method: 'POST',
        }),

      getJiraProjects: () =>
        this.request<Array<{
          id: string;
          key: string;
          name: string;
          avatarUrl: string;
        }>>('/api/admin/integrations/jira/projects'),

      // Intercom integration
      getIntercom: () =>
        this.request<{
          configured: boolean;
          connected: boolean;
          workspaceName?: string;
          workspaceId?: string;
          enabled: boolean;
          installedBy?: string;
          connectedAt?: string;
        }>('/api/admin/integrations/intercom'),

      updateIntercom: (data: Partial<{
        enabled: boolean;
      }>) =>
        this.request<{
          configured: boolean;
          connected: boolean;
          workspaceName?: string;
          workspaceId?: string;
          enabled: boolean;
          installedBy?: string;
          connectedAt?: string;
        }>('/api/admin/integrations/intercom', {
          method: 'PATCH',
          body: JSON.stringify(data),
        }),

      getIntercomAuthUrl: () =>
        this.request<{ url: string }>('/api/admin/integrations/intercom/auth'),

      disconnectIntercom: () =>
        this.request<{ message: string }>('/api/admin/integrations/intercom/disconnect', {
          method: 'POST',
        }),
    },

    // Jira issue links for posts
    jira: {
      search: (query: string) =>
        this.request<Array<{
          id: string;
          key: string;
          summary: string;
          status: string;
          statusCategory: string;
          issueType: { name: string; iconUrl: string };
          project: { key: string; name: string };
        }>>(`/api/admin/jira/search?query=${encodeURIComponent(query)}`),

      getPostIssues: (postId: string) =>
        this.request<Array<{
          id: string;
          issueKey: string;
          issueSummary: string | null;
          issueStatus: string | null;
          issueUrl: string;
          linkedFrom: string;
          created: string;
        }>>(`/api/admin/jira/posts/${postId}/jira-issues`),

      linkIssue: (postId: string, issueKey: string) =>
        this.request<{
          id: string;
          issueKey: string;
          issueSummary: string | null;
          issueStatus: string | null;
          issueUrl: string;
          linkedFrom: string;
          created: string;
        }>(`/api/admin/jira/posts/${postId}/jira-issues`, {
          method: 'POST',
          body: JSON.stringify({ issueKey }),
        }),

      unlinkIssue: (postId: string, issueKey: string) =>
        this.request<{ message: string }>(
          `/api/admin/jira/posts/${postId}/jira-issues/${encodeURIComponent(issueKey)}`,
          { method: 'DELETE' }
        ),
    },

    // Intercom conversation links for posts
    intercom: {
      search: (query: string) =>
        this.request<Array<{
          id: string;
          title: string | null;
          state: string;
          customerName: string | null;
          customerEmail: string | null;
          createdAt: string;
          url: string;
        }>>(`/api/admin/intercom/search?query=${encodeURIComponent(query)}`),

      getPostConversations: (postId: string) =>
        this.request<Array<{
          id: string;
          conversationId: string;
          conversationTitle: string | null;
          conversationState: string | null;
          customerName: string | null;
          customerEmail: string | null;
          conversationUrl: string;
          created: string;
        }>>(`/api/admin/intercom/posts/${postId}/conversations`),

      linkConversation: (postId: string, conversationId: string) =>
        this.request<{
          id: string;
          conversationId: string;
          conversationTitle: string | null;
          conversationState: string | null;
          customerName: string | null;
          customerEmail: string | null;
          conversationUrl: string;
          created: string;
        }>(`/api/admin/intercom/posts/${postId}/conversations`, {
          method: 'POST',
          body: JSON.stringify({ conversationId }),
        }),

      unlinkConversation: (postId: string, conversationId: string) =>
        this.request<{ message: string }>(
          `/api/admin/intercom/posts/${postId}/conversations/${encodeURIComponent(conversationId)}`,
          { method: 'DELETE' }
        ),
    },
  };

  // Upload endpoints
  upload = {
    image: async (file: File, type: 'posts' | 'comments' | 'avatars' = 'posts'): Promise<string> => {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('type', type);

      // Get CSRF token for POST request
      let csrfToken: string | undefined;
      try {
        csrfToken = await this.fetchCsrfToken();
      } catch {
        console.warn('Could not fetch CSRF token for upload');
      }

      const url = `${this.baseURL}/api/upload/image`;
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      let response = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers,
      });

      let data: ApiResponse<{ url: string }> = await response.json();

      // If CSRF token is invalid, clear it and retry once with fresh token
      if (response.status === 403 && data.code === 'CSRF_VALIDATION_FAILED') {
        this.clearCsrfToken();
        const newCsrfToken = await this.fetchCsrfToken();
        headers['X-CSRF-Token'] = newCsrfToken;

        // Recreate FormData for retry (can't reuse after fetch consumes it)
        const retryFormData = new FormData();
        retryFormData.append('image', file);
        retryFormData.append('type', type);

        response = await fetch(url, {
          method: 'POST',
          body: retryFormData,
          credentials: 'include',
          headers,
        });
        data = await response.json();
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to upload image');
      }

      return data.data!.url;
    },

    images: async (files: File[], type: 'posts' | 'comments' | 'avatars' = 'posts'): Promise<string[]> => {
      const formData = new FormData();
      files.forEach(file => formData.append('images', file));
      formData.append('type', type);

      // Get CSRF token for POST request
      let csrfToken: string | undefined;
      try {
        csrfToken = await this.fetchCsrfToken();
      } catch {
        console.warn('Could not fetch CSRF token for upload');
      }

      const url = `${this.baseURL}/api/upload/images`;
      const headers: Record<string, string> = {};
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      let response = await fetch(url, {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers,
      });

      let data: ApiResponse<{ urls: string[] }> = await response.json();

      // If CSRF token is invalid, clear it and retry once with fresh token
      if (response.status === 403 && data.code === 'CSRF_VALIDATION_FAILED') {
        this.clearCsrfToken();
        const newCsrfToken = await this.fetchCsrfToken();
        headers['X-CSRF-Token'] = newCsrfToken;

        // Recreate FormData for retry
        const retryFormData = new FormData();
        files.forEach(file => retryFormData.append('images', file));
        retryFormData.append('type', type);

        response = await fetch(url, {
          method: 'POST',
          body: retryFormData,
          credentials: 'include',
          headers,
        });
        data = await response.json();
      }

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to upload images');
      }

      return data.data!.urls;
    },
  };

  // Search endpoints
  search = {
    posts: (params: {
      q: string;
      boardId?: string;
      page?: number;
      limit?: number;
    }) => {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
      const query = queryParams.toString();
      return this.request<{ posts: any[]; pagination: any; query: string }>(`/api/search?${query}`);
    },
  };

  // Profile endpoints
  profile = {
    get: () =>
      this.request<{
        user: {
          id: string;
          email: string;
          name: string;
          alias: string;
          avatarURL: string | null;
          isAdmin: boolean;
          adminRole: string | null;
          emailVerified: boolean;
          created: string;
          lastActivity: string | null;
          _count: {
            posts: number;
            comments: number;
            votes: number;
          };
        };
      }>('/api/profile'),

    update: (data: { name?: string; alias?: string; avatarURL?: string | null }) =>
      this.request<{ user: any }>('/api/profile', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),

    changePassword: (data: { currentPassword: string; newPassword: string }) =>
      this.request<{ message: string }>('/api/profile/change-password', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    votes: (params?: { page?: number; limit?: number }) => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      const query = queryParams.toString();
      return this.request<{
        votes: Array<{
          votedAt: string;
          post: {
            id: string;
            title: string;
            slug: string;
            status: string;
            score: number;
            commentCount: number;
            voteCount: number;
            created: string;
            author: {
              id: string;
              name: string;
              alias: string;
              avatarURL: string | null;
            };
            board: {
              id: string;
              name: string;
              slug: string;
            };
          };
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`/api/profile/votes${query ? `?${query}` : ''}`);
    },

    activity: (params?: { page?: number; limit?: number }) => {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', String(params.page));
      if (params?.limit) queryParams.append('limit', String(params.limit));
      const query = queryParams.toString();
      return this.request<{
        activities: Array<{
          type: 'status_change' | 'new_comment';
          postTitle: string;
          postSlug: string;
          boardSlug: string;
          newStatus?: string;
          commentPreview?: string;
          actor: {
            id: string;
            name: string;
            alias: string;
            avatarURL: string | null;
          };
          isOwnPost: boolean;
          createdAt: string;
        }>;
        pagination: {
          page: number;
          limit: number;
          total: number;
          pages: number;
        };
      }>(`/api/profile/activity${query ? `?${query}` : ''}`);
    },
  };
}

export const api = new ApiClient(API_BASE_URL);
